import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../../assets/Icons";
import SmallPopupWithInput from "../../../../../components/_utils/CommonSmallPopupWithInput";
import StatusContainer from "../../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import CommonToast from "../../../../../components/_utils/CommonToast";
import UniversalRefreshToast from "../../../../../components/_utils/UniversalRefreshToast";
import {
  approveInternalAccount,
  internalAccountDetailsById,
  rejectInternalAccount,
} from "../../../../../redux/features/accounts/internalAccount";
import { setHideBreadCrumb } from "../../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import { GlobalConfiguration } from "../../../../../utils/configurationProvider";
import globalText from "../../../../../utils/globalText";

const InternalAccountEditSuccess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { internalAccountDetailsData } = useSelector(
    (state) => state.internalAccount,
  );

  const services = useSelector((state) => state.refreshToken.services);


  const [isFocused, setIsFocused] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [rejectPopup, setRejectPopup] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [currentAccountDetails, setCurrentAccountDetails] = useState({
    name: "",
    bank_name: "",
    ifsc_code: "",
    bank_acc_no: "",
    category_ids: "",
    id: "",
    pan_no: "",
    bank_account_holder_full_name: "",
  });


  const internalAccountSuccessData =
    internalAccountDetailsData?.internal_accounts[0];

  const canApprove =
    internalAccountSuccessData?.approval_status === "pending" &&
    services?.includes("IAAccount");

  const canCreateTxn = services?.includes("CInternalTxn");

  const computeVariant = (value, focused) => {
    const length = (value ?? "").trim().length;

    if (length === GlobalConfiguration?.lengthCheckZero) return "default";

    if (length >= GlobalConfiguration.rejectReasonMinLength) {
      return "verified";
    }
    return focused ? "typing" : "error";
  };

  const handleInputChange = (event) => {
    let value = event?.target?.value;
    value = value.replace(/[^a-zA-Z0-9\s]/g, "");
    value = value.replace(/^\s+/, "");
    value = value.replace(/\s{2,}/g, " ");
    value = value.slice(0, 150);
    setDeclineReason(value);
  };

  const handlers = {
    createTxn: () => {
      if (!services?.includes("CInternalTxn")) {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "CInternalTxn" },
        });
        return;
      }

      navigate("/internal_transaction_dashboard/create_internal_transaction", {
        state: currentAccountDetails,
        replace: true,
      });
    },
    viewDetails: () =>
      navigate(
        `/internal_account_dashboard/internal_account_view_all/internal_account_detail_page/${id}`,
        {
          replace: true,
        },
      ),
    dashboard: () =>
      navigate("/internal_account_dashboard", {
        replace: true,
      }),
  };

  const accountApproval = () => {
    (async () => {
      setShowLoader();
      toast.dismiss();
      UniversalRefreshToast("approvalaccountsRefresh");
      let response = await dispatch(approveInternalAccount({ id }));
      if (approveInternalAccount.fulfilled.match(response)) {
        toast.dismiss();
        CommonToast({
          text: globalText?.INTERNAL_ACCOUNT_APPROVED,
          variant: "var(--success)",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        });

        navigate(
          `/internal_account_dashboard/internal_account_view_all/internal_account_detail_page/${id}`,
          { replace: true },
        );
      } else {
        toast.dismiss();
        CommonToast({
          text: response?.payload?.errors?.internal_account?.[0],
          variant: "var(--error)",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        });
      }
    })();
  };

  const accountRejected = () => {
    (async () => {
      setShowLoader();
      toast.dismiss();
      UniversalRefreshToast("accountsRefresh");
      let response = await dispatch(
        rejectInternalAccount({
          id,
          rejection_reason: declineReason,
        }),
      );
      if (rejectInternalAccount.fulfilled.match(response)) {
        toast.dismiss();
        CommonToast({
          text: globalText?.INTERNAL_ACCOUNT_REJECTED,
          variant: "var(--success)",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        });

        navigate(
          `/internal_account_dashboard/internal_account_view_all/internal_account_detail_page/${id}`,
          { replace: true },
        );
      } else {
        toast.dismiss();
        CommonToast({
          text: response?.payload?.errors?.internal_account?.[0],
          variant: "var(--error)",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        });
        setRejectPopup(false);
      }
    })();
  };

  const primaryBtn = {
    enabledBgColor: "var(--text-primary)",
    enabledTextColor: "var(--positive-bg)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    imgColor: "var(--positive-bg)",
    hoverOption: "dark",
  };

  const outlineBtn = {
    enabledBgColor: "var(--bg-positive-bg)",
    enabledTextColor: "var(--text-primary)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    outLineColor: "var(--text-primary)",
    hoverOption: "dark",
  };

  const buttonRows = [
    ...(canApprove
      ? [
          [
            {
              btnText: globalText?.REJECT_ACCOUNT,
              enabledBgColor: "var(--negative-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS?.REJECT_ICON_RED_WITH_WHITE_CIRCLE,
              handleFunction: () => {
                setRejectPopup(true);
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
            },
            {
              btnText: globalText?.APPROVE_ACCOUNT,
              enabledBgColor: "var(--positive-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
              handleFunction: () => {
                accountApproval();
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
            },
          ],
        ]
      : []),

    [
      {
        ...primaryBtn,
        btnText: globalText?.CREATE_TRANSACTION,
        icon: ICONS?.ADD_BUTTON_WHITE,
        handleFunction: handlers.createTxn,
        isDisabled: !canCreateTxn,
      },
      {
        ...primaryBtn,
        btnText: globalText?.VIEW_DETAILS,
        icon: ICONS?.VIEW_DETAILS,
        handleFunction: handlers.viewDetails,
      },
      {
        ...outlineBtn,
        btnText: globalText?.GO_TO_DASHBOARD,
        icon: ICONS?.GO_TO_DASHBOARD,
        handleFunction: handlers.dashboard,
        imgColor: "var(--text-primary)",
      },
    ],
  ];

  const updatedFields = location?.state?.updatedFields || [];

  const contactFieldConfig = {
    account_name: {
      leftSubTitle: globalText?.ACCOUNT_NAME,
      getTitle: (acc) => (acc?.name ? acc.name : "Not available"),
    },
    contact_no: {
      leftSubTitle: globalText?.CONTACT_NUMBER,
      icon: ICONS?.CONTACT_WITH_WHITE_CIRCLE,
      getTitle: (acc) =>
        acc?.contact_no ? `+91 ${acc.contact_no}` : "Not available",
      iconSize: "small",
      bgNeeded: false,
    },
    email_id: {
      leftSubTitle: globalText?.EMAIL_ID,
      icon: ICONS?.CONTACT_US_MAIL,
      getTitle: (acc) => acc?.email_id || "Not available",
      iconSize: "small",
      bgNeeded: false,
    },
  };

  const getFieldsToShow = () => {
    if (updatedFields.length > GlobalConfiguration.lengthCheckZero) {
      return updatedFields;
    }

    return ["account_name", "contact_no", "email_id"];
  };

  const fieldsToShow = getFieldsToShow();
  const showOnlyUpdated = fieldsToShow;

  const renderInfoCards = () => {
    return fieldsToShow
      .filter((key) => contactFieldConfig[key])
      .map((key) => {
        const config = contactFieldConfig[key];
        return (
          <InfoCard
            key={key}
            bgColor="var(--positive-card)"
            rows={[
              {
                leftTitle: config.getTitle(internalAccountSuccessData),
                leftSubTitle: config.leftSubTitle,
                ...(config.icon && { icon: config.icon }),
                ...(config.iconSize && {
                  iconSize: config.iconSize,
                }),
                ...(config.bgNeeded === false && { bgNeeded: false }),
              },
            ]}
          />
        );
      });
  };

  useEffect(() => {
    dispatch(internalAccountDetailsById({ id: id }));
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [id, dispatch]);

 

  useEffect(() => {
    if (internalAccountSuccessData) {
      const {
        bank_name,
        bank_acc_no,
        category_ids,
        ifsc_code,
        id,
        pan_no,
        bank_account_holder_full_name,
        name,
      } = internalAccountSuccessData;
      setCurrentAccountDetails({
        bank_name,
        bank_acc_no,
        category_ids:category_ids?.filter((fil) => {
        return fil.category_name === "Internal";
      })[0],
        ifsc_code,
        id,
        pan_no,
        bank_account_holder_full_name,
        name,
      });
    }
  }, [internalAccountSuccessData]);
  return (
    <div>
      {rejectPopup && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-max-zindex bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <SmallPopupWithInput
            headerTitle={globalText.REJECT_REASON_LABEL}
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-md"
            secondHeaderTitle={globalText?.REJECT_REASON_PROMPT}
            secondHeaderFontColor="text-secondary"
            noteText={globalText?.REJECT_POPUP_ACCOUNT_NOTE_TEXT}
            secondHeaderTextSize="font-xs"
            secondHeaderFontWeight="normal"
            noteColor="text-secondary"
            inputPlaceholder={globalText?.REASON}
            inputVariant={computeVariant(declineReason, isFocused)}
            inputOnFocus={(e) => setIsFocused(true)}
            inputOnBlur={(e) => setIsFocused(false)}
            btnOneIsDisabled={showLoader ? true : false}
            inputBgColor="var(--primary-bg)"
            inputFgColor="var(--text-secondary)"
            inputOnInput={(e) => {
              if (!showLoader) {
                handleInputChange(e);
              }
            }}
            inputValue={declineReason}
            inputFontKind="big"
            inputIsFullWidth={true}
            btnOneText="Cancel"
            btnOneHandleFunction={() => {
              setRejectPopup(false);
              setDeclineReason("");
            }}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneHoverOption="white"
            btnTwoText={globalText?.REJECT}
            btnTwoHandleFunction={() => {
              accountRejected();
              setDeclineReason("");
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={
              declineReason.trim().length <
              GlobalConfiguration.rejectReasonMinLength
            }
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
            btnTwoInActiveVariantNeeded={true}
          />
        </div>
      )}
      <StatusContainer
        status={globalText?.SUCCESS?.toLowerCase()}
        buttonRows={buttonRows}
        title={globalText?.DETAILS_UPDATED_SUCCESSFULLY}
      >
        <TabsWeapperSection title="Account details">
          <>
            {showOnlyUpdated && renderInfoCards()}
            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: internalAccountSuccessData?.id,
                  leftSubTitle: "Account ID",
                  copyText: internalAccountSuccessData?.id,
                },
              ]}
            />
          </>
        </TabsWeapperSection>
      </StatusContainer>
    </div>
  );
};

export default InternalAccountEditSuccess;
